import { useMemo, useRef } from "react";
import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { classNames } from '@progress/kendo-react-common';
import {
    Icon,
    SearchFilters,
    SearchItems,
    SuspenseBoundary,
    DateTabs
} from 'components';
import { useAccountParam, useSearchValidation, useVisibility } from 'hooks';
import { useRefreshSearchResults, useSearchQuery, useSearchResultsFilter, useUserInfo } from 'state';
import styles from './SearchPage.module.scss';

export default function SearchPage() {
    const { dateRange, setDateRange } = useSearchResultsFilter();
    const refresh = useRefreshSearchResults();
    const accountId = useAccountParam();
    const [query] = useSearchQuery();
    const { isValid, errorMessage } = useSearchValidation(query);
    const { isInternalUser } = useUserInfo();

    const headerRef = useRef<HTMLDivElement | null>(null);
    const shouldShowCombinedHeader = useVisibility(headerRef);

    const Headers = useMemo(
        () => (
            <div className={styles.pageHeader}>
                {shouldShowCombinedHeader ? (
                    <div className={styles.title}>
                        <span>
                            <Icon icon={faSearch} />
                        </span>
                        <span>Search Results</span>
                    </div>
                ) : (
                    <></>
                )}
                <DateTabs
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                />
            </div>
        ),
        [dateRange, setDateRange, shouldShowCombinedHeader]
    );

    const Filters = useMemo(
        () => (
            <div className={styles.filters}>
                <SearchFilters />
            </div>
        ),
        []
    );

    const FiltersAndHeader = useMemo(
        () => (
            <div
                className={classNames(styles.fixedHeaderAndFilter, {
                    [styles.hidden]: shouldShowCombinedHeader,
                })}
            >
                {Filters}
                {Headers}
            </div>
        ),
        [Filters, Headers, shouldShowCombinedHeader]
    );

    return (
        <div className={styles.container}>
            <div
                className={classNames({
                    [styles.hidden]: !shouldShowCombinedHeader,
                })}
                ref={headerRef}
            >
                {Headers}
            </div>
            {FiltersAndHeader}
            <div className={styles.content}>
                <div
                    className={classNames(styles.filters, {
                        [styles.hidden]: !shouldShowCombinedHeader,
                    })}
                >
                    {Filters}
                </div>
                {!isValid && (
                    <div className={styles.searchError}>{errorMessage}</div>
                )}
                {isValid && (isInternalUser || accountId) ? (
                    <SuspenseBoundary
                        errorMessage={`Error retrieving search results ${accountId && `for account ${accountId}`}`}
                        onReset={refresh}
                    >
                        <SearchItems />
                    </SuspenseBoundary>
                ) : <div className={styles.message}>Please select an account to view search results.</div>}
            </div>
        </div>
    );
}
